import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Center, Loader } from "@mantine/core";
import { RequireAuth } from "@/guards/RequireAuth";
import { PublicOnly } from "@/guards/PublicOnly";
import { RequireAdmin } from "@/guards/RequireAdmin";
import { DashLayout } from "@/app/layouts/DashLayout";
import { FormLayout } from "@/app/layouts/FormLayout";
import { FormProvider as FormContextProvider } from "@/features/form/context/FormContext";
import { QuoteProvider as QuoteContextProvider } from "@/features/form/context/QuoteContext";
import { registerNavigate } from "@/services/navigation";
import { refreshToken } from "@/services/auth";

import { StorePage } from "@/features/store/pages/StorePage";
import { StoreCheckoutPage } from "@/features/store/pages/StoreCheckoutPage";

const SignInPage = lazy(() =>
  import("@/features/auth/pages/SignInPage").then((m) => ({ default: m.SignInPage }))
);
const SignUpPage = lazy(() =>
  import("@/features/auth/pages/SignUpPage").then((m) => ({ default: m.SignUpPage }))
);
const FormPage = lazy(() =>
  import("@/features/form/pages/FormPage").then((m) => ({ default: m.FormPage }))
);
const PaymentStatusPage = lazy(() =>
  import("@/features/payment/pages/PaymentStatusPage").then((m) => ({ default: m.PaymentStatusPage }))
);
const DashHomePage = lazy(() =>
  import("@/features/dashboard/pages/DashHomePage").then((m) => ({ default: m.DashHomePage }))
);
const MyPlansPage = lazy(() =>
  import("@/features/plan/pages/MyPlansPage").then((m) => ({ default: m.MyPlansPage }))
);
const MyPlanPage = lazy(() =>
  import("@/features/plan/pages/MyPlanPage").then((m) => ({ default: m.MyPlanPage }))
);
const ShipmentsPage = lazy(() =>
  import("@/features/shipment/pages/ShipmentsPage").then((m) => ({ default: m.ShipmentsPage }))
);
const ShipmentByIdPage = lazy(() =>
  import("@/features/shipment/pages/ShipmentByIdPage").then((m) => ({ default: m.ShipmentByIdPage }))
);
const MyAccountPage = lazy(() =>
  import("@/features/account/pages/MyAccountPage").then((m) => ({ default: m.MyAccountPage }))
);
const AdminDashboardPage = lazy(() =>
  import("@/features/admin/pages/AdminDashboardPage").then((m) => ({ default: m.AdminDashboardPage }))
);
const AdminUsersPage = lazy(() =>
  import("@/features/admin/pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage }))
);

function RouteFallback() {
  return (
    <Center h="100vh">
      <Loader color="dark" />
    </Center>
  );
}

function NavigateRegistrar() {
  const navigate = useNavigate();

  useEffect(() => {
    registerNavigate(navigate);
  }, [navigate]);

  return null;
}

const SESSION_REFRESH_INTERVAL_MS = 20 * 60 * 1000;

function SessionRefresher() {
  useEffect(() => {
    const interval = setInterval(() => {
      // Silent: a 401 here just means there's no active session yet (e.g.
      // still on the "account" step of the form), which is expected and
      // not worth surfacing to the user.
      refreshToken().catch(() => {});
    }, SESSION_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  return null;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <NavigateRegistrar />
      <SessionRefresher />
      <Routes>
        <Route path="/" element={<StorePage />} />
        <Route path="/checkout" element={<QuoteContextProvider><StoreCheckoutPage /></QuoteContextProvider>} />

        <Route element={<PublicOnly />}>
          <Route path="/auth/sign-in" element={<SignInPage />} />
          <Route path="/auth/sign-up" element={<SignUpPage />} />
        </Route>

        <Route
          element={
            <QuoteContextProvider>
              <FormContextProvider>
                <FormLayout />
              </FormContextProvider>
            </QuoteContextProvider>
          }
        >
          <Route path="/form" element={<FormPage />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route path="/payment-status" element={<PaymentStatusPage />} />

          <Route element={<DashLayout />}>
            <Route path="/dash/home" element={<DashHomePage />} />
            <Route path="/dash/my-plans" element={<MyPlansPage />} />
            <Route path="/dash/my-plans/:planId" element={<MyPlanPage />} />
            <Route path="/dash/shipments" element={<ShipmentsPage />} />
            <Route path="/dash/shipments/:id" element={<ShipmentByIdPage />} />
            <Route path="/dash/my-account" element={<MyAccountPage />} />
            <Route element={<RequireAdmin />}>
              <Route path="/dash/admin" element={<AdminDashboardPage />} />
              <Route path="/dash/admin/users" element={<AdminUsersPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/auth/sign-in" replace />} />
      </Routes>
    </Suspense>
  );
}
